/**
 * LandingPage.Components.js
 * Sub-componentes, hooks y constantes extraídos de LandingPage.js
 * para mantener el archivo principal manejable.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, Star } from "lucide-react";
import { API } from "../utils/api";

/* ── Hooks ─────────────────────────────────────────────────────────────────── */
export function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Magnetic Button ────────────────────────────────────────────────────────── */
export function MagBtn({ children, className, onClick, testId, disabled, type = "button" }) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width  / 2) * 0.22;
    const y = (e.clientY - r.top  - r.height / 2) * 0.22;
    el.style.transform = `translate(${x}px,${y}px) scale(1.03)`;
  }, []);
  const onLeave = () => { if (ref.current) ref.current.style.transform = ""; };
  return (
    <button ref={ref} type={type} disabled={disabled}
      onMouseMove={onMove} onMouseLeave={onLeave}
      onClick={onClick} data-testid={testId}
      className={`btn-magnetic btn-glow-border ${className}`}>
      {children}
    </button>
  );
}

/* ── Mouse Trail (canvas) ──────────────────────────────────────────────────── */
export function MouseTrail() {
  const canvasRef = useRef(null);
  const trailRef = useRef([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const onMove = (e) => {
      trailRef.current.push({ x: e.clientX, y: e.clientY, life: 1.0, t: Date.now() });
      if (trailRef.current.length > 40) trailRef.current.shift();
    };
    window.addEventListener("mousemove", onMove);
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const now = Date.now();
      trailRef.current = trailRef.current.filter(p => now - p.t < 1400);
      trailRef.current.forEach((p, i) => {
        const age = (now - p.t) / 1400;
        const alpha = (1 - age) * 0.55;
        const size = (1 - age) * 4;
        const prev = trailRef.current[i - 1];
        if (prev && i > 0) {
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(p.x, p.y);
          ctx.strokeStyle = `rgba(255,192,0,${alpha * 0.5})`;
          ctx.lineWidth = size * 0.8;
          ctx.lineCap = "round";
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,192,0,${alpha})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <canvas ref={canvasRef} style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:9998, opacity:0.7 }} />;
}

/* ── Matrix Rain (canvas background) ────────────────────────────────────────── */
export function MatrixRain() {
  const ref = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize();
    const CHARS = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉ0123456789ABCDEF@#$%';
    const SZ = 13;
    let cols = Math.floor(c.width / SZ);
    let drops = Array.from({ length: cols }, () => Math.random() * -60);
    let frameN = 0;
    const tick = () => {
      frameN++;
      ctx.fillStyle = 'rgba(13,13,13,0.06)';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.font = `${SZ}px 'Courier New', monospace`;
      drops.forEach((y, i) => {
        if (y < 0) { drops[i] += 0.25; return; }
        const ch = CHARS[Math.floor(Math.random() * CHARS.length)];
        const bright = y < 2;
        ctx.fillStyle = bright ? 'rgba(180,255,180,0.95)' : `rgba(0,${180 + Math.random()*75 | 0},60,0.8)`;
        ctx.fillText(ch, i * SZ, y * SZ);
        if (frameN % 3 === 0 && Math.random() > 0.85) drops[i]++;
        if (y * SZ > c.height && Math.random() > 0.975) drops[i] = Math.random() * -30;
      });
    };
    const iv = setInterval(tick, 80);
    const onResize = () => { resize(); cols = Math.floor(c.width / SZ); drops = Array.from({length:cols},()=>Math.random()*-60); };
    window.addEventListener('resize', onResize);
    return () => { clearInterval(iv); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={ref} style={{ position:'fixed', inset:0, zIndex:1, opacity:0.055, pointerEvents:'none' }}/>;
}

/* ── Audio Player ────────────────────────────────────────────────────────────── */
export function AudioPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume]   = useState(0.3);
  const [showVol, setShowVol] = useState(false);
  const playedRef = useRef(false);

  useEffect(() => {
    const tryPlay = () => {
      if (!playedRef.current && audioRef.current) {
        playedRef.current = true;
        audioRef.current.volume = 0.3;
        audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
      }
    };
    window.addEventListener('scroll', tryPlay, { once:true, passive:true });
    window.addEventListener('click',  tryPlay, { once:true });
    return () => { window.removeEventListener('scroll',tryPlay); window.removeEventListener('click',tryPlay); };
  }, []);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().then(()=>setPlaying(true)).catch(()=>{}); }
  };
  const changeVol = v => { const n=parseFloat(v); setVolume(n); if(audioRef.current) audioRef.current.volume=n; };

  return (
    <div data-testid="audio-player" style={{
      position:'fixed', bottom:88, right:20, zIndex:9000,
      display:'flex', flexDirection:'column', alignItems:'center', gap:8,
      background:'rgba(10,10,10,0.9)', backdropFilter:'blur(12px)',
      border:'1px solid rgba(255,199,0,0.2)', borderRadius:12, padding:'10px 8px',
    }}>
      <audio ref={audioRef} src="/audio/bg-music.wav" loop preload="none"/>
      <span style={{ fontSize:12, color:'#ffc700', lineHeight:1 }}>♪</span>
      <button onClick={toggle} style={{ background:'none',border:'none',cursor:'pointer',color:'white',padding:0,display:'flex',alignItems:'center' }}>
        {playing
          ? <svg width="14" height="14" viewBox="0 0 12 12" fill="white"><rect x="2" y="1" width="3" height="10" rx="1"/><rect x="7" y="1" width="3" height="10" rx="1"/></svg>
          : <svg width="14" height="14" viewBox="0 0 12 12" fill="white"><polygon points="2,1 10,6 2,11"/></svg>
        }
      </button>
      <button onClick={()=>setShowVol(v=>!v)} style={{ background:'none',border:'none',cursor:'pointer',padding:0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill={volume===0?'#444':'rgba(255,255,255,0.6)'}>
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
        </svg>
      </button>
      {showVol && (
        <input type="range" min="0" max="1" step="0.05" value={volume}
          onChange={e=>changeVol(e.target.value)}
          style={{ writingMode:'vertical-lr', direction:'rtl', height:60, width:18, accentColor:'#ffc700', cursor:'pointer' }}/>
      )}
    </div>
  );
}

/* ── WhatsApp Float Button ──────────────────────────────────────────────────── */
export function WhatsAppFloat({ waNumber }) {
  const [open, setOpen] = useState(false);
  const waUrl = waNumber
    ? `https://wa.me/${waNumber.replace(/\D/g,"")}?text=${encodeURIComponent("¡Hola! Vi SitiosYA y quisiera consultar sobre tener mi web profesional.")}`
    : "#";

  const BUBBLES = [
    "¡Hola! 👋 Bienvenido a SitiosYA",
    "¿Querés tu web profesional lista hoy?",
    "Respondemos en minutos ⚡",
  ];

  return (
    <div style={{ position:"fixed", bottom:20, right:20, zIndex:9999, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:10 }}>
      {open && (
        <div style={{ width:280, background:"#fff", borderRadius:16, overflow:"hidden", boxShadow:"0 8px 40px rgba(0,0,0,0.35)", animation:"wa-slide-up 0.22s ease-out both" }}>
          <div style={{ background:"#1DA851", padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:"50%", background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>🐰</div>
            <div>
              <p style={{ color:"white", fontWeight:700, fontSize:14, margin:0 }}>SitiosYA</p>
              <p style={{ color:"rgba(255,255,255,0.75)", fontSize:11, margin:0 }}>Responde en &lt; 10 min</p>
            </div>
            <button onClick={() => setOpen(false)} style={{ marginLeft:"auto", background:"none", border:"none", color:"rgba(255,255,255,0.7)", fontSize:20, cursor:"pointer", lineHeight:1, padding:0 }}>×</button>
          </div>
          <div style={{ background:"#ece5dd", padding:"12px 10px", display:"flex", flexDirection:"column", gap:8 }}>
            {BUBBLES.map((msg, i) => (
              <div key={`bubble-${i}`} style={{ alignSelf:"flex-start", maxWidth:"88%", background:"white", borderRadius:"0 10px 10px 10px", padding:"8px 12px", fontSize:12.5, color:"#111", boxShadow:"0 1px 2px rgba(0,0,0,0.1)", animation:`wa-bubble-in 0.28s ease ${i*0.1}s both` }}>{msg}</div>
            ))}
          </div>
          <div style={{ padding:10, background:"white" }}>
            <a href={waUrl} target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, background:"#25D366", color:"white", padding:"10px 16px", borderRadius:8, fontWeight:700, fontSize:13, textDecoration:"none", boxShadow:"0 2px 8px rgba(37,211,102,0.3)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Iniciar chat por WhatsApp
            </a>
          </div>
        </div>
      )}
      <button data-testid="whatsapp-float-btn" onClick={() => setOpen(o => !o)} style={{ width:56, height:56, borderRadius:"50%", background:"linear-gradient(135deg,#25D366,#128C7E)", border:"none", cursor:"pointer", position:"relative", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(37,211,102,0.45)", transition:"transform 0.2s ease, box-shadow 0.2s ease" }}
        onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.12)";e.currentTarget.style.boxShadow="0 6px 28px rgba(37,211,102,0.6)";}}
        onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 4px 20px rgba(37,211,102,0.45)";}}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        {!open && <span style={{ position:"absolute", top:0, right:0, width:14, height:14, borderRadius:"50%", background:"#ff4545", border:"2px solid #0D0D0D", animation:"pulse-dot 1.8s ease-in-out infinite" }}/>}
      </button>
    </div>
  );
}

/* ── Marquee Strip ──────────────────────────────────────────────────────────── */
export const MARQUEE = ["CAFETERÍAS","PELUQUERÍAS","GIMNASIOS","ESTUDIOS","RESTAURANTES","CONSULTORAS","BOUTIQUES","CLÍNICAS","TALLERES","TIENDAS"];

export function MarqueeStrip() {
  const [paused, setPaused] = useState(false);
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div className="overflow-hidden border-y border-white/[0.05] py-4 bg-[#080808] cursor-default"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="marquee-track gap-10" style={{ animationPlayState: paused ? "paused" : "running" }}>
        {items.map((item, i) => (
          <span key={`marquee-${i}`} className="text-xs font-black uppercase tracking-[0.2em] text-gray-600 px-6 whitespace-nowrap">
            {i % 2 === 0 ? <span className="text-[#ffc700]">✦</span> : <span className="text-[#FF4F00]">✦</span>} {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Web Cube ─────────────────────────────────────────────────────────────── */
export function WebCube({ rx, ry }) {
  const S = 160;
  return (
    <div className="cube-wrapper flex items-center justify-center">
      <div className="cube" style={{ width: S, height: S, transform: `rotateX(${rx}deg) rotateY(${ry}deg)`, transition: "transform 0.08s linear" }}>
        <div className="cube-face front" style={{ background: "#0D0D0D", overflow: "hidden" }}>
          <div style={{ background: "#111", height: 18, display: "flex", alignItems: "center", paddingLeft: 6, gap: 3, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            {["#FF5F57","#FFBD2E","#28C840"].map(c => <div key={c} style={{ width: 5, height: 5, borderRadius: "50%", background: c }} />)}
            <div style={{ flex: 1, height: 3, background: "#1e1e1e", borderRadius: 2, margin: "0 5px", display:"flex", alignItems:"center", paddingLeft:4 }}>
              <div style={{ width: 60, height: 2, background: "rgba(255,255,255,0.15)", borderRadius: 1 }} />
            </div>
          </div>
          <div style={{ height: 55, background: "linear-gradient(135deg,#0074b7 0%,#001a2e 100%)", padding: "6px 7px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 3 }}>
            <div style={{ fontSize: 0, display: "flex", alignItems: "center", gap: 3 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: "#ffc700" }} />
              <div style={{ width: 30, height: 2, background: "rgba(255,255,255,0.3)", borderRadius: 1 }} />
            </div>
            <div style={{ width: "75%", height: 5, background: "rgba(255,255,255,0.95)", borderRadius: 2 }} />
            <div style={{ width: "55%", height: 3, background: "rgba(255,255,255,0.45)", borderRadius: 2 }} />
            <div style={{ width: 28, height: 8, background: "#ffc700", borderRadius: 3, marginTop: 1 }} />
          </div>
          <div style={{ padding: "5px 7px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 3 }}>
            {[["rgba(255,199,0,0.18)","#ffc700"],["rgba(0,116,183,0.22)","#0074b7"],["rgba(255,79,0,0.18)","#FF4F00"]].map(([bg, ac]) => (
              <div key={bg} style={{ height: 30, background: bg, borderRadius: 4, border: `1px solid ${ac}30`, display:"flex", flexDirection:"column", padding:"4px 3px", gap:2 }}>
                <div style={{ width: "70%", height: 2, background: ac, borderRadius: 1, opacity: 0.8 }} />
                <div style={{ width: "90%", height: 1.5, background: "rgba(255,255,255,0.1)", borderRadius: 1 }} />
              </div>
            ))}
          </div>
          <div style={{ padding: "4px 7px", display: "flex", gap: 3 }}>
            <div style={{ width: 32, height: 7, background: "#ffc700", borderRadius: 2 }} />
            <div style={{ width: 24, height: 7, background: "rgba(255,255,255,0.06)", borderRadius: 2, border: "1px solid rgba(255,255,255,0.1)" }} />
          </div>
        </div>
        <div className="cube-face back" style={{ background: "#050d14", overflow: "hidden", padding: 7 }}>
          <div style={{ border: "1px solid rgba(0,116,183,0.5)", borderRadius: 3, height: "100%", padding: 4, display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ display: "flex", gap: 3, alignItems: "center", paddingBottom: 3, borderBottom: "1px dashed rgba(0,116,183,0.3)" }}>
              <div style={{ width: 8, height: 8, border: "1px solid #0074b7", borderRadius: 2 }} />
              {[24,18,22].map((w)=><div key={w} style={{ width: w, height: 2, background: "rgba(0,116,183,0.35)", borderRadius: 1 }} />)}
            </div>
            <div style={{ flex: 1, border: "1px dashed rgba(0,116,183,0.25)", borderRadius: 2, padding: 3, display:"flex", flexDirection:"column", gap:2 }}>
              <div style={{ width: "80%", height: 4, background: "rgba(0,116,183,0.2)", borderRadius: 1 }} />
              <div style={{ width: "55%", height: 3, background: "rgba(0,116,183,0.15)", borderRadius: 1 }} />
              <div style={{ width: 20, height: 6, background: "rgba(255,199,0,0.3)", borderRadius: 2, border: "1px solid rgba(255,199,0,0.5)" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2 }}>
              {[1,2,3].map(i=><div key={i} style={{ height: 20, border: "1px dashed rgba(0,116,183,0.25)", borderRadius: 2 }} />)}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", border: "1px solid #0074b7", background: "rgba(0,116,183,0.2)" }} />
              <div style={{ width: 55, height: 1.5, background: "rgba(0,116,183,0.3)", borderRadius: 1 }} />
            </div>
          </div>
        </div>
        <div className="cube-face right" style={{ background: "#0f0f0f", overflow: "hidden", padding: 8, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ width: 60, height: 110, border: "2px solid rgba(255,255,255,0.15)", borderRadius: 8, overflow: "hidden", background: "#111" }}>
            <div style={{ background: "#111", height: 8, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <div style={{ width: 14, height: 2, background: "rgba(255,255,255,0.15)", borderRadius: 2 }} />
            </div>
            <div style={{ background: "linear-gradient(180deg,#0074b7 0%,#001a2e 100%)", height: 32, padding: 4, display:"flex", flexDirection:"column", gap:2, justifyContent:"center" }}>
              <div style={{ width: "70%", height: 3, background: "rgba(255,255,255,0.9)", borderRadius: 1 }} />
              <div style={{ width: "45%", height: 2, background: "rgba(255,255,255,0.4)", borderRadius: 1 }} />
              <div style={{ width: 18, height: 5, background: "#ffc700", borderRadius: 2 }} />
            </div>
            <div style={{ padding: 3, display:"flex", flexDirection:"column", gap:2 }}>
              {[1,2,3].map(i=><div key={i} style={{ height: 14, background: "rgba(255,255,255,0.04)", borderRadius: 3, border:"1px solid rgba(255,255,255,0.06)" }} />)}
            </div>
          </div>
        </div>
        <div className="cube-face left" style={{ background: "#0a0a0a", overflow: "hidden", padding: 8, display: "flex", flexDirection: "column", gap: 4, justifyContent: "center" }}>
          <div style={{ display:"flex", gap:3 }}>
            {["#ffc700","#0074b7","#FF4F00","#ffffff","#111"].map((c)=>(
              <div key={c} style={{ flex:1, height:14, background:c, borderRadius:3, border:"1px solid rgba(255,255,255,0.1)" }} />
            ))}
          </div>
          {[["80%","4","rgba(255,255,255,0.9)"],["60%","3","rgba(255,255,255,0.5)"],["50%","2","rgba(255,255,255,0.3)"],["70%","2","rgba(255,199,0,0.6)"]].map(([w,h,c])=>(
            <div key={c} style={{ width:w, height:`${h}px`, background:c, borderRadius:1 }} />
          ))}
          <div style={{ display:"flex", gap:3, marginTop:2 }}>
            {[20,28,22].map((w)=><div key={w} style={{ width:w, height:2, background:"rgba(255,255,255,0.08)", borderRadius:1 }} />)}
          </div>
        </div>
        <div className="cube-face top" style={{ background: "#111", overflow: "hidden", display: "flex", flexDirection:"column", padding: "5px 7px", gap:3 }}>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: "#ffc700" }} />
            <div style={{ flex: 1, display: "flex", gap: 5 }}>
              {[30,22,28,18].map((w) => <div key={w} style={{ width: w, height: 2, background: "rgba(255,255,255,0.18)", borderRadius: 1 }} />)}
            </div>
            <div style={{ width:22, height:7, background:"#ffc700", borderRadius:3 }} />
          </div>
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ width:"80%", height:2, background:"rgba(255,255,255,0.05)", borderRadius:1 }} />
          </div>
        </div>
        <div className="cube-face bottom" style={{ background: "#050808", overflow: "hidden", padding: "5px 7px", display: "flex", flexDirection:"column", gap:2 }}>
          {[["rgba(0,116,183,0.8)","60%",0],["rgba(255,199,0,0.6)","80%",1],["rgba(255,255,255,0.12)","45%",2],["rgba(0,116,183,0.5)","70%",3],["rgba(255,79,0,0.5)","35%",4]].map(([c,w,idx])=>(
            <div key={c} style={{ display:"flex", gap:3, paddingLeft:`${idx*6}px` }}>
              <div style={{ width:w, height:2, background:c, borderRadius:1 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Data constants ─────────────────────────────────────────────────────────── */
export const SHOWCASE = [
  { num:"01", title:"CAFETERÍA", subtitle:"Menú digital y reservas online.", img:"https://images.pexels.com/photos/2174069/pexels-photo-2174069.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", color:"#FF4F00", features:["Menú online actualizable","Reservas en línea","Fotos profesionales"] },
  { num:"02", title:"PELUQUERÍA", subtitle:"Agenda de turnos online 24/7.", img:"https://images.pexels.com/photos/3916361/pexels-photo-3916361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", color:"#ffc700", features:["Turnos online 24/7","Galería de trabajos","Precios y servicios"] },
  { num:"03", title:"GYM / CROSSFIT", subtitle:"Gestión de clases y pagos.", img:"https://images.pexels.com/photos/6388373/pexels-photo-6388373.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", color:"#0074b7", features:["Horario de clases","Planes y precios","Videos de rutinas"] },
  { num:"04", title:"ESTUDIO LEGAL", subtitle:"Profesionalismo y contacto.", img:"https://images.pexels.com/photos/5668806/pexels-photo-5668806.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", color:"#ffc700", features:["Áreas de práctica","Formulario de consulta","Credenciales y logros"] },
];

export const STEPS = [
  { num:"01", title:"Completás el formulario", desc:"Nos contás tu negocio, servicios y cómo querés que se vea tu marca." },
  { num:"02", title:"La IA construye tu sitio", desc:"El Empleado de IA Diseñador crea tu sitio completo en 5 minutos." },
  { num:"03", title:"Revisás y ajustás", desc:"Accedés al panel y le decís a la IA qué cambiar. Lo hace en segundos." },
  { num:"04", title:"Tu negocio online", desc:"Publicamos con tu dominio .com. Clientes te encuentran en Google desde el día 1." },
];

export const TESTIMONIALS = [
  { name:"Carolina P.", biz:"Café & Pastelería", text:"En 3 semanas ya me contactaron 12 clientes nuevos por Google. Nunca pensé que una web haría tanta diferencia.", stars:5 },
  { name:"Rodrigo M.", biz:"Estudio Jurídico", text:"Mis colegas se sorprendieron de lo profesional que quedó. El soporte es increíble, me respondieron en 10 minutos.", stars:5 },
  { name:"Valeria T.", biz:"Centro Estético", text:"La IA actualiza mis promociones cuando quiero. Es como tener un equipo de marketing por el precio de un café al día.", stars:5 },
];

/* ── StepItem ───────────────────────────────────────────────────────────────── */
export function StepItem({ step, index }) {
  const isEven = index % 2 === 0;
  const [ref, visible] = useReveal(0.2);
  return (
    <div ref={ref} className={`reveal-item delay-${(index%4)+1} ${visible?"visible":""} flex items-start gap-6 sm:gap-0 relative sm:grid sm:grid-cols-2 pb-12 sm:pb-16`}>
      <div className="absolute left-7 sm:left-1/2 top-0 -translate-x-1/2 flex items-center justify-center z-10">
        <div className={`w-4 h-4 rounded-full border-2 transition-all duration-500 ${visible?"bg-[#ffc700] border-[#ffc700]":"bg-[#0D0D0D] border-white/20"}`} />
      </div>
      <div className={`pl-14 sm:pl-0 ${isEven?"sm:col-start-1 sm:pr-16 sm:text-right":"sm:col-start-2 sm:pl-16"} sm:flex sm:flex-col sm:justify-center`}>
        <span className="text-[10px] font-black font-mono uppercase tracking-[0.2em] text-[#ffc700] block mb-2">{step.num}</span>
        <h3 className="text-xl sm:text-2xl font-black text-white mb-2">{step.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed max-w-xs">{step.desc}</p>
      </div>
      <div className={isEven?"hidden sm:block sm:col-start-2":"hidden sm:block sm:col-start-1"} />
    </div>
  );
}

/* ── Plan checkout helper ────────────────────────────────────────────────────── */
export async function startPlanCheckout(plan) {
  const origin = window.location.origin;
  const ref = new URLSearchParams(window.location.search).get("ref") || "";
  const res = await fetch(`${API}/payments/checkout-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan, origin_url: origin, referral_code: ref }),
  });
  if (!res.ok) throw new Error("Error al iniciar el pago");
  const { checkout_url } = await res.json();
  window.location.href = checkout_url;
}
